import React, { useEffect, useState } from "react";
import { api, formatErr, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { PageHeader, StatusPill, EmptyState, readImageAsBase64 } from "@/components/shared";
import PhotoUpload from "@/components/PhotoUpload";
import { Plus, Search, Edit3, Trash2, ShieldAlert, ImagePlus, Phone, Mail, MapPin, FileText, MessageCircle, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

const empty = {
  full_name: "",
  phone: "",
  email: "",
  address: "",
  license_number: "",
  license_expiration: "",
  license_photo: null,
  license_photo_back: null,
  insurance_photo: null,
  emergency_contact: "",
  notes: "",
  high_risk: false,
};

export default function Customers() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);

  const load = () => api.get("/customers").then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing({ ...empty });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing({ ...empty, ...c });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...editing };
      // strip id from payload (use route)
      const id = payload.id;
      delete payload.id;
      delete payload.created_at;
      if (id) {
        await api.put(`/customers/${id}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", payload);
        toast.success("Customer added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Customer deleted");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const showHistory = async (c) => {
    setDetail(c);
    const r = await api.get(`/customers/${c.id}/history`);
    setHistory(r.data);
  };

  const filtered = rows
    .filter((r) =>
      [r.full_name, r.phone, r.email, r.license_number].join(" ").toLowerCase().includes(q.toLowerCase()),
    )
    .sort((a, b) => (b.amount_owed || 0) - (a.amount_owed || 0));

  const smsHref = (phone, body) => {
    const num = (phone || "").replace(/[^\d+]/g, "");
    const sep = /iPhone|iPad|Mac/i.test(navigator.userAgent) ? "&" : "?";
    return `sms:${num}${sep}body=${encodeURIComponent(body)}`;
  };

  const paymentReminderBody = (c) =>
    `Hi ${(c.full_name || "").split(" ")[0]}, this is Fair Rideshare LLC — a friendly reminder that you have a payment due. Please send via Zelle/Cash App to (215) 770-0679 or in person. Reply when paid. Thanks!`;

  const handleFile = async (key, file) => {
    if (!file) return;
    const b64 = await readImageAsBase64(file);
    setEditing((s) => ({ ...s, [key]: b64 }));
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader
        title="Customers"
        subtitle="Renters & profiles"
        testId="customers-page-header"
        action={
          <Button data-testid="add-customer-button" onClick={openNew} className="bg-black hover:bg-gray-800 text-white">
            <Plus size={16} className="mr-2" /> Add Customer
          </Button>
        }
      />

      <div className="mb-4 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          data-testid="customer-search-input"
          placeholder="Search by name, phone, email, license…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9"
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No customers yet"
          description="Add your first customer to get started."
          testId="customers-empty"
          action={<Button onClick={openNew} data-testid="empty-add-customer">Add customer</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="customers-list">
          {filtered.map((c) => (
            <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition">
              <div className="flex items-start justify-between gap-3">
                <button onClick={() => showHistory(c)} className="text-left flex-1" data-testid={`customer-row-${c.id}`}>
                  <div className="font-heading font-semibold text-base">{c.full_name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{c.license_number || "No DL on file"}</div>
                </button>
                <div className="flex items-center gap-1.5">
                  {c.amount_owed > 0 && (
                    <span
                      className={`pill ${c.amount_owed >= 500 ? "pill-red" : "pill-amber"}`}
                      title="Total accrued back-rent across active rentals"
                      data-testid={`customer-owed-${c.id}`}
                    >
                      Owes {money(c.amount_owed)}
                    </span>
                  )}
                  {c.high_risk && (
                    <span className="pill pill-red" title="High risk">
                      <ShieldAlert size={11} /> High risk
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-3 space-y-1 text-sm">
                {c.phone && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Phone size={12} className="text-gray-400" /> {c.phone}
                  </div>
                )}
                {c.email && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Mail size={12} className="text-gray-400" /> {c.email}
                  </div>
                )}
                {c.address && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <MapPin size={12} className="text-gray-400" /> <span className="line-clamp-1">{c.address}</span>
                  </div>
                )}
                {c.license_expiration && (
                  <div className="text-xs text-gray-500">License expires {fmtDate(c.license_expiration)}</div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 pt-3 border-t border-gray-100">
                <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}>
                  <Edit3 size={12} className="mr-1.5" /> Edit
                </Button>
                {c.phone && (
                  <a
                    href={smsHref(c.phone, paymentReminderBody(c))}
                    data-testid={`text-payment-reminder-${c.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-900 px-2.5 py-1.5 rounded-md"
                  >
                    <MessageCircle size={12} /> Pay reminder
                  </a>
                )}
                <Button size="sm" variant="ghost" onClick={() => showHistory(c)} data-testid={`history-customer-${c.id}`}>
                  <FileText size={12} className="mr-1.5" /> History
                </Button>
                {isAdmin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => del(c.id)}
                    data-testid={`delete-customer-${c.id}`}
                  >
                    <Trash2 size={12} />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing?.id ? "Edit Customer" : "Add Customer"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Full name</Label>
                <Input data-testid="cust-input-full-name" value={editing.full_name} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="cust-input-phone" value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} placeholder="+1234567890" />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="cust-input-email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Address</Label>
                <Input data-testid="cust-input-address" value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} />
              </div>
              <div>
                <Label>Driver license #</Label>
                <Input data-testid="cust-input-license" value={editing.license_number} onChange={(e) => setEditing({ ...editing, license_number: e.target.value })} />
              </div>
              <div>
                <Label>License expiration</Label>
                <Input type="date" data-testid="cust-input-license-exp" value={editing.license_expiration || ""} onChange={(e) => setEditing({ ...editing, license_expiration: e.target.value })} />
              </div>
              <div>
                <Label>Emergency contact</Label>
                <Input data-testid="cust-input-emergency" value={editing.emergency_contact} onChange={(e) => setEditing({ ...editing, emergency_contact: e.target.value })} />
              </div>
              <div className="flex items-center gap-3 pt-7">
                <Switch
                  data-testid="cust-input-high-risk"
                  checked={editing.high_risk}
                  onCheckedChange={(v) => setEditing({ ...editing, high_risk: v })}
                />
                <Label className="m-0">Flag as high-risk</Label>
              </div>

              <div className="md:col-span-2">
                <div className="overline mb-2">Driver's license &amp; insurance</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <PhotoUpload
                    testId="cust-input-license-front"
                    label="License — front"
                    value={editing.license_photo}
                    onChange={(v) => setEditing({ ...editing, license_photo: v })}
                  />
                  <PhotoUpload
                    testId="cust-input-license-back"
                    label="License — back"
                    value={editing.license_photo_back}
                    onChange={(v) => setEditing({ ...editing, license_photo_back: v })}
                  />
                  <PhotoUpload
                    testId="cust-input-insurance-photo"
                    label="Insurance card"
                    value={editing.insurance_photo}
                    onChange={(v) => setEditing({ ...editing, insurance_photo: v })}
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Textarea data-testid="cust-input-notes" value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} rows={3} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cust-cancel">Cancel</Button>
            <Button onClick={save} className="bg-black hover:bg-gray-800 text-white" data-testid="cust-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && (setDetail(null), setHistory(null))}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="customer-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{detail?.full_name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div><div className="overline">Phone</div><div>{detail.phone || "—"}</div></div>
                <div><div className="overline">Email</div><div>{detail.email || "—"}</div></div>
                <div><div className="overline">License</div><div>{detail.license_number || "—"}</div></div>
                <div><div className="overline">License exp</div><div>{fmtDate(detail.license_expiration)}</div></div>
              </div>
              {detail.notes && (
                <div>
                  <div className="overline mb-1">Notes</div>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{detail.notes}</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {detail.license_photo && (
                  <div>
                    <div className="overline mb-1">License — front</div>
                    <img src={detail.license_photo} alt="dl front" className="rounded border max-h-40" />
                  </div>
                )}
                {detail.license_photo_back && (
                  <div>
                    <div className="overline mb-1">License — back</div>
                    <img src={detail.license_photo_back} alt="dl back" className="rounded border max-h-40" />
                  </div>
                )}
                {detail.insurance_photo && (
                  <div>
                    <div className="overline mb-1">Insurance Card</div>
                    <img src={detail.insurance_photo} alt="ins" className="rounded border max-h-40" />
                  </div>
                )}
              </div>
              <div>
                <div className="overline mb-1">Rental History</div>
                {history?.reservations?.length ? (
                  <div className="border border-gray-200 rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                        <tr>
                          <th className="text-left px-3 py-2">Pickup</th>
                          <th className="text-left px-3 py-2">Vehicle</th>
                          <th className="text-left px-3 py-2">Status</th>
                          <th className="text-left px-3 py-2">Agreement</th>
                          <th className="text-right px-3 py-2">Outstanding</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {history.reservations.map((r) => (
                          <tr key={r.id}>
                            <td className="px-3 py-2">{fmtDate(r.pickup_datetime)}</td>
                            <td className="px-3 py-2">{r.vehicle_label || "—"}</td>
                            <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                            <td className="px-3 py-2">
                              {r.agreement_signed && r.signing_token ? (
                                <a
                                  href={`${process.env.REACT_APP_BACKEND_URL}/api/public/agreement/${r.signing_token}/pdf`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`history-signed-pdf-${r.id}`}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium hover:bg-emerald-100 transition"
                                  title="Signed — tap to open PDF"
                                >
                                  <FileCheck2 size={12} /> Archived
                                </a>
                              ) : (
                                <span className="text-xs text-gray-400" data-testid={`history-unsigned-${r.id}`}>Unsigned</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{money(r.balance_due)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No rentals yet.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
